/**
 * Icon — one consistent inline-SVG icon set (24px grid, 1.75 stroke, round joins,
 * currentColor). Replaces the ad-hoc unicode/emoji glyphs that read at mismatched
 * weights/metrics. Each icon is a semantic name; size defaults to 20px.
 */
import type { CSSProperties, ReactNode } from "react";

export type IconName =
  // nav / sections
  | "templates"
  | "generate"
  | "brands"
  | "mockups"
  | "settings"
  | "editor"
  | "admin"
  | "dashboard"
  // ui / actions
  | "info"
  | "bolt"
  | "power"
  | "close"
  | "copy"
  | "lock"
  | "unlock"
  | "check"
  | "check-circle"
  | "plus"
  | "minus"
  | "minus-circle"
  | "pause"
  | "play"
  | "chevron-down"
  | "chevron-right"
  | "chevron-up"
  | "arrow-left"
  | "arrow-right"
  | "arrow-up"
  | "arrow-down"
  | "upload"
  | "download"
  | "refresh"
  | "undo"
  | "redo"
  | "trash"
  | "search"
  | "grid"
  | "list"
  | "expand"
  | "warning"
  | "dot"
  | "menu"
  | "filter"
  | "folder"
  // domain
  | "phone"
  | "image"
  | "brush"
  | "target"
  | "shield"
  | "sparkle"
  | "star"
  | "brain";

const PATHS: Record<IconName, ReactNode> = {
  templates: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.6" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" />
    </>
  ),
  generate: (
    <>
      <path d="M12 4.5l1.6 5.9 5.9 1.6-5.9 1.6L12 19.5l-1.6-5.9L4.5 12l5.9-1.6z" />
      <path d="M19 3.5l.6 2 2 .6-2 .6-.6 2-.6-2-2-.6 2-.6z" />
    </>
  ),
  brands: (
    <>
      <path d="M3.6 12.2l8.2-8.2a1.8 1.8 0 0 1 1.3-.5h5a1.8 1.8 0 0 1 1.8 1.8v5a1.8 1.8 0 0 1-.5 1.3l-8.2 8.2a1.8 1.8 0 0 1-2.6 0l-5-5a1.8 1.8 0 0 1 0-2.6z" />
      <circle cx="16.3" cy="7.7" r="1.15" />
    </>
  ),
  mockups: (
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.6" />
      <path d="M10.5 18.4h3" />
    </>
  ),
  settings: (
    <>
      <path d="M4 6h9" />
      <path d="M17 6h3" />
      <circle cx="15" cy="6" r="2" />
      <path d="M4 12h3" />
      <path d="M11 12h9" />
      <circle cx="9" cy="12" r="2" />
      <path d="M4 18h7" />
      <path d="M15 18h5" />
      <circle cx="13" cy="18" r="2" />
    </>
  ),
  editor: (
    <>
      <path d="M4 20l1-4L16 5a2.1 2.1 0 0 1 3 3L8 19z" />
      <path d="M13.5 6.5l3 3" />
    </>
  ),
  admin: (
    <>
      <path d="M12 3l7 2.6v5.2c0 4.3-3 7.8-7 9.7-4-1.9-7-5.4-7-9.7V5.6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  dashboard: (
    <>
      <path d="M3 21h18" />
      <rect x="5" y="10.5" width="3.4" height="8.5" rx="1" />
      <rect x="10.3" y="5.5" width="3.4" height="13.5" rx="1" />
      <rect x="15.6" y="14" width="3.4" height="5" rx="1" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 7.8h.01" />
    </>
  ),
  bolt: <path d="M13 3L5 13h5l-1 8 8-10h-5z" />,
  power: (
    <>
      <path d="M12 3v9" />
      <path d="M6.5 7a8 8 0 1 0 11 0" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>
  ),
  check: <path d="M4.5 12.5l5 5 10-11" />,
  /* WS-Z9 / F-4: "In batch" is a DONE state, and it used to be drawn with the same bare
     `check` as Approve — which co-render on a Pending template that is already in a batch, two
     identical glyphs in one cell meaning two different things. The ring is the difference, and
     it is the same distinction `minus-circle` draws against `minus`. */
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 12.2l2.8 2.8L16 9.5" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  minus: <path d="M5 12h14" />,
  /* WS-Z9 fix: "Remove" drawn as a bare `minus` reads as a SEPARATOR, not a control —
     measured on the Templates index, where the dash between two framed icon buttons looked
     like the gap between them. The ring is what makes it a button at 16px. */
  "minus-circle": (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8 12h8" />
    </>
  ),
  /* WS-Z9 rule 13: the Queue row's Pause / Resume pair. The icon set had no transport glyphs
     because nothing had ever been icon-only; both are drawn on the same 24 grid as the rest. */
  pause: (
    <>
      <path d="M9.5 5v14" />
      <path d="M14.5 5v14" />
    </>
  ),
  play: <path d="M7.5 4.8l11 7.2-11 7.2z" />,
  "chevron-down": <path d="M5 9l7 7 7-7" />,
  "chevron-right": <path d="M9 5l7 7-7 7" />,
  "chevron-up": <path d="M5 15l7-7 7 7" />,
  "arrow-left": (
    <>
      <path d="M20 12H4" />
      <path d="M11 5l-7 7 7 7" />
    </>
  ),
  "arrow-right": (
    <>
      <path d="M4 12h16" />
      <path d="M13 5l7 7-7 7" />
    </>
  ),
  "arrow-up": (
    <>
      <path d="M12 20V4" />
      <path d="M5 11l7-7 7 7" />
    </>
  ),
  "arrow-down": (
    <>
      <path d="M12 4v16" />
      <path d="M5 13l7 7 7-7" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M4 20h16" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v12" />
      <path d="M7 11l5 5 5-5" />
      <path d="M4 20h16" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 11a8 8 0 0 0-14-4.5L4 8" />
      <path d="M4 4v4h4" />
      <path d="M4 13a8 8 0 0 0 14 4.5L20 16" />
      <path d="M20 20v-4h-4" />
    </>
  ),
  undo: (
    <>
      <path d="M9 7L4 12l5 5" />
      <path d="M4 12h11a5 5 0 0 1 0 10h-1" />
    </>
  ),
  redo: (
    <>
      <path d="M15 7l5 5-5 5" />
      <path d="M20 12H9a5 5 0 0 0 0 10h1" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M9 7V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v2" />
      <path d="M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4.3-4.3" />
    </>
  ),
  grid: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.4" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.4" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.4" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.4" />
    </>
  ),
  list: (
    <>
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3.5 6h.01" />
      <path d="M3.5 12h.01" />
      <path d="M3.5 18h.01" />
    </>
  ),
  expand: (
    <>
      <path d="M4 9V4h5" />
      <path d="M20 15v5h-5" />
      <path d="M15 4h5v5" />
      <path d="M9 20H4v-5" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3.5L21.5 20H2.5z" />
      <path d="M12 10v4" />
      <path d="M12 17.5h.01" />
    </>
  ),
  dot: <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />,
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  filter: (
    <>
      <path d="M4 5h16" />
      <path d="M7 12h10" />
      <path d="M10 19h4" />
    </>
  ),
  // A folder, drawn as its tab and body — the destination row's "Open folder" (rule 13). The
  // tab is a separate stroke rather than one outline so it still reads as a folder at 16px,
  // where a single-path tab collapses into the top edge.
  folder: (
    <>
      <path d="M3.25 7.4a1.9 1.9 0 0 1 1.9-1.9h3.35l1.9 2.2h8.45a1.9 1.9 0 0 1 1.9 1.9v8.1a1.9 1.9 0 0 1-1.9 1.9H5.15a1.9 1.9 0 0 1-1.9-1.9z" />
      <path d="M3.25 9.7h17.5" />
    </>
  ),
  phone: (
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.6" />
      <path d="M10.5 18.4h3" />
    </>
  ),
  image: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.2" />
      <circle cx="8.5" cy="9.5" r="1.6" />
      <path d="M5 17l4.5-4.5 3.5 3.5L16 13l3.5 3.5" />
    </>
  ),
  brush: (
    <>
      <path d="M14 6l4 4L9 19l-5 1 1-5z" />
      <path d="M12.5 7.5l4 4" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.4" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7 2.6v5.2c0 4.3-3 7.8-7 9.7-4-1.9-7-5.4-7-9.7V5.6z" />
    </>
  ),
  sparkle: <path d="M12 4.5l1.6 5.9 5.9 1.6-5.9 1.6L12 19.5l-1.6-5.9L4.5 12l5.9-1.6z" />,
  /* MI-4: added because CopyButton was rendering "⧉" — a Unicode glyph at a different
     weight and baseline from every drawn icon beside it. The point of an icon SET is that
     a missing name is a reason to DRAW one, never to fall back to a character. */
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2.5" />
      <path d="M5 15V6a2 2 0 012-2h9" />
    </>
  ),
  /* MI-4: replaces `content: "🔒"` — an EMOJI, which renders in the platform's colour font
     and ignores currentColor entirely. */
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 018 0v3" />
    </>
  ),
  /* WS-Z9 rule 13: "Unblock" is the inverse of "Block", and a row action that is only ever an
     icon needs the two states to differ at a glance — the same padlock with the shackle swung
     open, not a second metaphor. */
  unlock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 017.7-1.5" />
    </>
  ),
  /* WS-Z9 / F-2: "Make default" on a brand row. A star is THE conventional mark for the one
     of N that is the default, and the set had no glyph for it — `check` is Approve's and would
     have said "this is approved" on a row where approval is a different control entirely. */
  star: <path d="M12 4.2l2.4 5 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 10l5.4-.8z" />,
  brain: (
    <>
      <path d="M9 4.5a3 3 0 0 0-3 3 3 3 0 0 0-1.5 5.4A3 3 0 0 0 6 18a3 3 0 0 0 6 .3V5.5a3 3 0 0 0-3-1z" />
      <path d="M15 4.5a3 3 0 0 1 3 3 3 3 0 0 1 1.5 5.4A3 3 0 0 1 18 18a3 3 0 0 1-6 .3" />
    </>
  ),
};

export function Icon({
  name,
  size = 20,
  className,
  style,
}: {
  name: IconName;
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      className={className}
      style={style}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
